import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Block,
  LocationOn,
  Person,
  Email,
  Phone,
  School,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { branchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/ConfirmDialog';

const BranchList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDistrict, setFilterDistrict] = useState('all');
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, branch: null, deleting: false });

  const districts = [
    'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
    'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
    'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod'
  ];

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 1000 };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterDistrict !== 'all') params.district = filterDistrict;

      const response = await branchAPI.getAll(params);
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDistrict]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleApproveBranch = (branchId) => {
    setConfirmDialog({
      open: true,
      title: 'Approve Branch',
      message: 'Are you sure you want to approve this branch? This will activate the branch admin account.',
      onConfirm: async () => {
        try {
          await branchAPI.approve(branchId);
          toast.success('Branch approved successfully! Admin has been notified via email.');
          fetchBranches();
        } catch (error) {
          console.error('Error approving branch:', error);
          toast.error(error.response?.data?.message || 'Failed to approve branch');
        }
      },
    });
  };

  const handleOpenRejectDialog = (branch) => {
    setSelectedBranch(branch);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleRejectBranch = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await branchAPI.reject(selectedBranch._id, rejectionReason);
      toast.success('Branch rejected');
      setOpenRejectDialog(false);
      fetchBranches();
    } catch (error) {
      console.error('Error rejecting branch:', error);
      toast.error('Failed to reject branch');
    }
  };

  const handleToggleBlock = (branchId, currentStatus) => {
    const action = currentStatus === 'blocked' ? 'unblock' : 'block';
    setConfirmDialog({
      open: true,
      title: `${action === 'block' ? 'Block' : 'Unblock'} Branch`,
      message: `Are you sure you want to ${action} this branch?`,
      onConfirm: async () => {
        try {
          await branchAPI.toggleBlock(branchId);
          toast.success(`Branch ${action}ed successfully`);
          fetchBranches();
        } catch (error) {
          console.error(`Error ${action}ing branch:`, error);
          toast.error(`Failed to ${action} branch`);
        }
      },
    });
  };

  const handleDeleteBranch = (branch) => {
    setDeleteDialog({ open: true, branch, deleting: false });
  };

  const handleConfirmDelete = async () => {
    setDeleteDialog((prev) => ({ ...prev, deleting: true }));
    try {
      await branchAPI.delete(deleteDialog.branch._id);
      toast.success('Branch permanently disabled and admin account deleted');
      setDeleteDialog({ open: false, branch: null, deleting: false });
      fetchBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error(error.response?.data?.message || 'Failed to disable branch');
      setDeleteDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'blocked':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle fontSize="small" />;
      case 'rejected':
        return <Cancel fontSize="small" />;
      case 'blocked':
        return <Block fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Branches / ATC Centers
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/branches/create')}
          >
            Add Branch
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>District</InputLabel>
              <Select
                value={filterDistrict}
                onChange={(e) => setFilterDistrict(e.target.value)}
                label="District"
              >
                <MenuItem value="all">All Districts</MenuItem>
                {districts.map((district) => (
                  <MenuItem key={district} value={district}>
                    {district}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {branches.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="info">
            No branches found. {isSuperAdmin && 'Click "Add Branch" to create your first branch.'}
          </Alert>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {branches.map((branch) => {
            const activeCourseCount = branch.courseAssignments?.filter(ca => ca.status === 'active').length || 0;

            return (
              <Grid item xs={12} md={6} lg={4} key={branch._id}>
                <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {branch.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {branch.code}
                        </Typography>
                      </Box>
                      <Chip
                        label={branch.status?.toUpperCase()}
                        color={getStatusColor(branch.status)}
                        size="small"
                        icon={getStatusIcon(branch.status)}
                      />
                    </Box>

                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {branch.address && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                          <Typography variant="body2" color="text.secondary">
                            {[branch.address.street, branch.address.city, branch.address.district, branch.address.pincode]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        </Box>
                      )}

                      {branch.admin && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {branch.admin.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {branch.admin.email}
                            </Typography>
                          </Box>
                          {branch.admin.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {branch.admin.phone}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}

                      {activeCourseCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {activeCourseCount} Course{activeCourseCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}

                    {branch.status === 'rejected' && branch.rejectionReason && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Rejection Reason:</strong> {branch.rejectionReason}
                        </Typography>
                      </Alert>
                    )}
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    {isSuperAdmin && branch.status === 'pending' && (
                      <>
                        <Tooltip title="Approve Branch">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleApproveBranch(branch._id)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Branch">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleOpenRejectDialog(branch)}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {isSuperAdmin && (branch.status === 'approved' || branch.status === 'blocked') && (
                      <Tooltip title={branch.status === 'blocked' ? 'Unblock Branch' : 'Block Branch'}>
                        <IconButton
                          color={branch.status === 'blocked' ? 'success' : 'warning'}
                          size="small"
                          onClick={() => handleToggleBlock(branch._id, branch.status)}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box>
                    <Tooltip title="Edit Branch">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/admin/branches/edit/${branch._id}`)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {isSuperAdmin && (
                      <Tooltip title="Permanently Disable Branch">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteBranch(branch)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Rejection Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Branch</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide a reason for rejecting <strong>{selectedBranch?.name}</strong>:
            </Typography>
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectBranch} variant="contained" color="error">
            Reject Branch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmColor="primary"
      />

      {/* Permanent Delete Warning Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleteDialog.deleting && setDeleteDialog({ open: false, branch: null, deleting: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <Warning color="error" />
          Permanently Disable Branch
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>This action cannot be undone.</strong>
          </Alert>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            You are about to permanently disable <strong>{deleteDialog.branch?.name}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The following will happen immediately:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 0 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              The branch will be <strong>permanently banned</strong> and will no longer appear in the admin panel.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              The branch admin account will be <strong>permanently deleted</strong> — they will not be able to log in.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              The admin's email address will be freed and can be used for a new branch later.
            </Typography>
            <Typography component="li" variant="body2">
              All student data and records for this branch are <strong>preserved</strong> and not deleted.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, branch: null, deleting: false })} disabled={deleteDialog.deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteDialog.deleting}>
            {deleteDialog.deleting ? <CircularProgress size={20} color="inherit" /> : 'Permanently Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchList;
